import {
    Box,
    Card,
    Icon,
    IconButton,
    Typography,
    useAppSelector,
    useAppDispatch,
    useTheme,
} from '../../../base';
import {
    StyledNotificationsContainer,
    StyledNotificationsIconWrapper,
    StyledNotificationsIcon,
    StyledCardHeader,
    StyledClearAllButton,
    StyledDateText,
    StyledFlagIcon,
    StyledHeaderContent,
    StyledAlertItem,
    StyledSkeleton,
    StyledWrapperCard,
} from './DashboardAlerts.styles';
import { useTranslation } from 'react-i18next';
import { dashboardActions, dashboardSelectors } from '../../../actions';

const DashboardAlerts = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const dispatch = useAppDispatch();

    const notifications = useAppSelector(dashboardSelectors.getNotifications);
    const notificationsLoading = useAppSelector(
        dashboardSelectors.getIsLoadingNotifications
    );

    const handleClearAll = () => {
        dispatch(dashboardActions.updateNotificationsRead({ readAll: true }));
    };

    const handleClearAlert = (id: string) => {
        dispatch(dashboardActions.updateNotificationsRead({ id }));
    };

    const renderEmptyState = () => {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Icon
                    name="NoAlertsIconBig"
                    size={85}
                    color={theme.palette.primary.main}
                />
                <Typography variant="h4">
                    {t('dashboardPage.noAlertsAvailable')}
                </Typography>
            </Box>
        );
    };

    return (
        <>
            {notificationsLoading ? (
                <StyledSkeleton variant="rectangular" height={210} />
            ) : (
                <StyledWrapperCard>
                    <StyledCardHeader>
                        <StyledHeaderContent>
                            <StyledNotificationsIconWrapper
                                edge="end"
                                size="small"
                                onClick={() => {}}
                                disabled
                            >
                                <StyledNotificationsIcon
                                    name={'NotificationsIcon'}
                                    size={28}
                                />
                            </StyledNotificationsIconWrapper>
                            <Typography variant="h6">
                                {t('dashboardPage.alerts')}
                            </Typography>
                        </StyledHeaderContent>
                        <StyledClearAllButton
                            onClick={handleClearAll}
                            disabled={notifications.length === 0}
                            startIcon={
                                <Icon
                                    name="CloseIcon"
                                    size={16}
                                    color={theme.palette.error.main}
                                />
                            }
                        >
                            {t('dashboardPage.alertsClearAll')}
                        </StyledClearAllButton>
                    </StyledCardHeader>

                    <StyledNotificationsContainer>
                        {notifications.length === 0
                            ? renderEmptyState()
                            : notifications.map((notification) => (
                                  <StyledAlertItem key={notification.id}>
                                      <StyledFlagIcon
                                          severity={notification.severity}
                                      >
                                          <Icon
                                              name="OutlinedFlagIcon"
                                              size={20}
                                          />
                                      </StyledFlagIcon>
                                      <StyledDateText variant="body1">
                                          {notification.date}
                                      </StyledDateText>
                                      <Typography
                                          variant="body2"
                                          sx={{
                                              flex: 1,
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                              display: '-webkit-box',
                                              WebkitLineClamp: 2,
                                              WebkitBoxOrient: 'vertical',
                                          }}
                                      >
                                          {notification.description}
                                      </Typography>
                                      <Box
                                          sx={{
                                              paddingInlineEnd:
                                                  theme.spacing(1),
                                          }}
                                      >
                                          <IconButton
                                              size="small"
                                              onClick={() =>
                                                  handleClearAlert(
                                                      notification.id
                                                  )
                                              }
                                              sx={{
                                                  height: 24,
                                                  width: 24,
                                              }}
                                          >
                                              <Icon
                                                  name="CloseIcon"
                                                  size={14}
                                              />
                                          </IconButton>
                                      </Box>
                                  </StyledAlertItem>
                              ))}
                    </StyledNotificationsContainer>
                </StyledWrapperCard>
            )}
        </>
    );
};

export default DashboardAlerts;
